/**
 * Travel member product display-name SSOT (UX-04-B).
 * Account identifiers (memberId / username) stay unchanged for API/auth/routes.
 * This module is display-only presentation policy.
 */

/** Matches backend tourist username: guest_{digits}{countryEnName} (e.g. guest_188103386America). */
const TECHNICAL_GUEST_USERNAME = /^guest_(\d+)([A-Za-z][A-Za-z ]*)$/;

export const TRAVEL_IDENTITY_GENERIC_TRAVELER = '旅人';
export const TRAVEL_IDENTITY_GENERIC_MEMBER = '成員';
export const TRAVEL_IDENTITY_GENERIC_APPLICANT = '申請者';
export const TRAVEL_IDENTITY_GENERIC_COMPANION = '旅伴';
export const TRAVEL_IDENTITY_GENERIC_HOST = '主辦人';

function trimText(value) {
  return String(value ?? '').trim();
}

/**
 * Strict technical guest username detector.
 * Does not match fuzzy includes("guest_") or bare "guest" substrings.
 */
export function isTechnicalGuestUsername(value) {
  const text = trimText(value);
  if (!text) return false;
  return TECHNICAL_GUEST_USERNAME.test(text);
}

/**
 * Stable product label for a technical guest username.
 * Uses last 3 digits of the numeric segment: guest_188103386America → 旅人 386.
 */
export function technicalGuestProductDisplayName(username) {
  const text = trimText(username);
  const match = text.match(TECHNICAL_GUEST_USERNAME);
  if (!match) return TRAVEL_IDENTITY_GENERIC_TRAVELER;
  const digits = match[1];
  const suffix = digits.slice(-3);
  if (!suffix) return TRAVEL_IDENTITY_GENERIC_TRAVELER;
  return `${TRAVEL_IDENTITY_GENERIC_TRAVELER} ${suffix}`;
}

function usableNonTechnicalLabel(value) {
  const text = trimText(value);
  if (!text) return '';
  if (isTechnicalGuestUsername(text)) return '';
  return text;
}

function firstTechnicalGuestSource(member) {
  if (!member || typeof member !== 'object') return '';
  return [member.nickname, member.displayName, member.username]
    .map(trimText)
    .find((value) => isTechnicalGuestUsername(value)) || '';
}

/**
 * Product display name from a partial member / auth / author summary.
 * Priority: nickname → displayName → non-technical username → guest safe → generic.
 */
export function travelMemberProductDisplayName(member, options = {}) {
  const generic = trimText(options.genericFallback) || TRAVEL_IDENTITY_GENERIC_TRAVELER;
  if (member == null || typeof member !== 'object') return generic;

  const nickname = usableNonTechnicalLabel(member.nickname);
  if (nickname) return nickname;

  const displayName = usableNonTechnicalLabel(member.displayName);
  if (displayName) return displayName;

  const username = usableNonTechnicalLabel(member.username);
  if (username) return username;

  const technical = firstTechnicalGuestSource(member);
  if (technical) return technicalGuestProductDisplayName(technical);

  return generic;
}

/**
 * Short operational tag from memberId without using full Snowflake as primary name.
 */
export function memberIdentityShortTag(memberId) {
  const id = trimText(memberId);
  if (!id) return '';
  return id.length <= 4 ? id : id.slice(-4);
}

/**
 * GroupTrip applicant / companion / host row when summary may be missing.
 * Prefer product name from summary; otherwise role label (+ short tag for applicant/companion only).
 * Host fallback never shows raw Snowflake ID.
 */
export function groupTripMemberFacingLabel({
  role = 'companion',
  memberId,
  summary,
} = {}) {
  const roleFallback = role === 'applicant'
    ? TRAVEL_IDENTITY_GENERIC_APPLICANT
    : role === 'host'
      ? TRAVEL_IDENTITY_GENERIC_HOST
      : TRAVEL_IDENTITY_GENERIC_COMPANION;

  if (summary && typeof summary === 'object') {
    return travelMemberProductDisplayName(summary, { genericFallback: roleFallback });
  }

  if (role === 'host') {
    return roleFallback;
  }

  const tag = memberIdentityShortTag(memberId);
  return tag ? `${roleFallback} ${tag}` : roleFallback;
}
